import {
  UserPlus,
  User,
  IdentificationCard,
  Stethoscope,
  Gear,
  Folder,
  Check,
  Warning,
} from "@phosphor-icons/react";
import ModalCloseButton from "../../ui/ModalCloseButton";
import ConfirmDialog from "../../ui/ConfirmDialog";
import { GlobalLoader, HelpButton } from "../../shared";
import { NoAccessState } from "../../shared/access";
import PatientFilesStagingTab from "../PatientFilesStagingTab";
import usePatientForm, { PATIENT_TABS } from "./usePatientForm";
import BasicStep from "./steps/BasicStep";
import DetailsStep from "./steps/DetailsStep";
import MedicalStep from "./steps/MedicalStep";
import AdminStep from "./steps/AdminStep";
import "./CreatePatientWizard.css";

const TABS = [
  {
    id: PATIENT_TABS.BASIC,
    label: "פרטים בסיסיים",
    icon: User,
    required: true,
  },
  { id: PATIENT_TABS.DETAILS, label: "פרטים נוספים", icon: IdentificationCard },
  { id: PATIENT_TABS.MEDICAL, label: "רקע רפואי", icon: Stethoscope },
  { id: PATIENT_TABS.ADMIN, label: "ניהול ולו״ז", icon: Gear },
  { id: PATIENT_TABS.FILES, label: "תיק מטופל", icon: Folder },
];

/**
 * New-patient creation popup. Rendered by the /dashboard/patients/new route
 * (CreatePatientPage) as a full-viewport overlay + centered card — same
 * visual language as the appointment wizard, independent CSS namespace
 * (pat-wiz-*), but navigated with freely clickable tabs, not steps.
 *
 * Quick-save: "שמור מטופל" always available — name + phone is enough to
 * create a patient; every other tab is optional.
 */
function CreatePatientWizard() {
  const form = usePatientForm();
  const {
    activeTab,
    setActiveTab,
    errors,
    stagedData,
    handleStagedDataChange,
    handleSubmit,
    handleSafeClose,
    handleDiscardChanges,
    showCloseWarning,
    setShowCloseWarning,
    isLoading,
    isUploadingFiles,
    isChecking,
    saveDisabled,
    PlanLimitModalComponent,
  } = form;

  const isSaving = isLoading || isUploadingFiles;
  const stagedCount = stagedData.files.length + stagedData.folders.length;

  // Secretary without patients.createForOthers (or in a solo clinic): no
  // therapist to create the patient for — say so instead of a dead-end form.
  if (form.creationBlocked) {
    return (
      <div className="modal-overlay pat-wiz-overlay">
        <div className="pat-wiz-modal" role="dialog" aria-modal="true">
          <div className="pat-wiz-header">
            <div className="pat-wiz-header-actions">
              <ModalCloseButton onClick={handleSafeClose} />
            </div>
          </div>
          <NoAccessState
            size="page"
            message="כדי להוסיף מטופל/ת חדש/ה יש צורך בהרשאה ליצירת מטופלים עבור מטפלים אחרים. פנו למנהל/ת הקליניקה."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay pat-wiz-overlay">
      {/* Global loader for permission check, saving, and file uploads */}
      {isChecking && <GlobalLoader message="בודק הרשאות..." />}
      {isSaving && <GlobalLoader message="שומר מטופל..." />}

      {/* Plan limit modal */}
      <PlanLimitModalComponent />

      {/* form-container class kept — the onboarding tour anchors to
          `.create-patient-page .form-container` */}
      <div className="pat-wiz-modal form-container">
        <div className="pat-wiz-header">
          <div className="pat-wiz-title">
            <div className="pat-wiz-title-icon">
              <UserPlus size={20} weight="fill" />
            </div>
            <div className="pat-wiz-title-text">
              <span className="pat-wiz-eyebrow">מטופלים</span>
              <h3>הוספת מטופל חדש</h3>
            </div>
          </div>
          <div className="pat-wiz-header-actions">
            <HelpButton featureKey="create_patient" />
            <ModalCloseButton onClick={handleSafeClose} />
          </div>
        </div>

        <div className="pat-wiz-tabs" role="tablist">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`pat-wiz-tab ${isActive ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon size={15} weight={isActive ? "fill" : "regular"} />
                <span className="pat-wiz-tab-label">{tab.label}</span>
                {tab.required && (
                  <span className="pat-wiz-tab-required">חובה</span>
                )}
                {tab.id === PATIENT_TABS.FILES && stagedCount > 0 && (
                  <span className="pat-wiz-tab-badge">{stagedCount}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="pat-wiz-body">
          {activeTab === PATIENT_TABS.BASIC && <BasicStep form={form} />}
          {activeTab === PATIENT_TABS.DETAILS && <DetailsStep form={form} />}
          {activeTab === PATIENT_TABS.MEDICAL && <MedicalStep form={form} />}
          {activeTab === PATIENT_TABS.ADMIN && <AdminStep form={form} />}

          {/* Files staging keeps its own local state — it must stay mounted
              across tab changes, so it's hidden with CSS, never unmounted */}
          <div
            className={`pat-wiz-files-host ${
              activeTab === PATIENT_TABS.FILES ? "" : "pat-wiz-files-host--hidden"
            }`}
          >
            <PatientFilesStagingTab
              onStagedDataChange={handleStagedDataChange}
            />
          </div>

          {errors.submit && (
            <div className="pat-wiz-submit-error">
              <Warning size={18} weight="fill" />
              <span>{errors.submit}</span>
            </div>
          )}
        </div>

        <div className="pat-wiz-footer">
          <button
            type="button"
            className="pat-wiz-btn primary pat-wiz-save-btn"
            onClick={() => handleSubmit(false)}
            disabled={saveDisabled}
          >
            <Check size={16} weight="bold" />
            <span>{isSaving ? "שומר..." : "שמור מטופל"}</span>
          </button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showCloseWarning}
        onClose={() => setShowCloseWarning(false)}
        onConfirm={() => {
          setShowCloseWarning(false);
          handleDiscardChanges();
        }}
        title="יש שינויים שלא נשמרו"
        message="האם אתה בטוח שברצונך לצאת? הפרטים שהוזנו יימחקו."
        confirmText="יציאה"
        cancelText="המשך עריכה"
        type="warning"
      />
    </div>
  );
}

export default CreatePatientWizard;
